// Which bodies NAMES labels, and where each lands on screen. Pure: no DOM, no
// scene, no camera object. `src/scene/bodyLabels.ts` is the layer that draws
// what this decides.

/** The fields of a registry row the set is decided on. */
export interface LabelRow {
  readonly id: string;
  readonly parent: string | null;
}

/**
 * The local system's primary: the frame parent when it has children in the
 * registry, else its own parent unless that is the Sun. Null in heliocentric
 * cruise, where nothing beyond the major set is named.
 */
export function localPrimary(rows: readonly LabelRow[], frameParentId: string | null): string | null {
  if (frameParentId === null) return null;
  const f = rows.find((r) => r.id === frameParentId);
  if (f === undefined || f.parent === null) return null;
  if (rows.some((r) => r.parent === f.id)) return f.id;
  const p = rows.find((r) => r.id === f.parent);
  return p === undefined || p.parent === null ? null : p.id;
}

/**
 * The ids NAMES labels, in registry order: the Sun and every row orbiting it
 * directly, the local primary and its children, and the `always` ids (the
 * pilot target and the GO trip) wherever they are. `all` names every row,
 * which is the check's own control.
 */
export function namedIds(
  rows: readonly LabelRow[], frameParentId: string | null,
  always: readonly (string | null)[], all = false,
): string[] {
  const sun = rows.find((r) => r.parent === null)?.id ?? null;
  const primary = localPrimary(rows, frameParentId);
  const extra = new Set(always.filter((id): id is string => id !== null));
  return rows.filter((r) => all
    || r.parent === null
    || r.parent === sun
    || (primary !== null && (r.id === primary || r.parent === primary))
    || extra.has(r.id)).map((r) => r.id);
}

/**
 * Who wins a collision: the pilot target, then the GO trip, then the frame
 * parent, then the major bodies by radius, then the rest by radius.
 */
export function labelPriority(
  id: string, radiusM: number, major: boolean,
  roles: { targetId: string | null; goId: string | null; frameParentId: string | null },
): number {
  if (id === roles.targetId) return 4e30;
  if (id === roles.goId) return 3e30;
  if (id === roles.frameParentId) return 2e30;
  return (major ? 1e20 : 0) + Math.max(0, radiusM);
}

/** One body as the camera sees it, in CSS px, y down. */
export interface Projected {
  /** In front of the eye at all. */
  readonly front: boolean;
  readonly px: number;
  readonly py: number;
  /** Drawn radius of the body's disc. */
  readonly discPx: number;
  /** Metres from the eye to the body's centre. */
  readonly distM: number;
}

/**
 * A camera-space offset (x right, y up, -z forward, metres) through a
 * symmetric perspective. `tanHalf` is tan of half the vertical fov.
 */
export function projectBody(
  x: number, y: number, z: number, radiusM: number,
  tanHalf: number, aspect: number, width: number, height: number,
): Projected {
  const distM = Math.hypot(x, y, z);
  const front = z < 0;
  const depth = Math.max(1e-9, -z);
  const ndcX = x / depth / (tanHalf * aspect);
  const ndcY = y / depth / tanHalf;
  // Inside the body's own radius the disc covers everything; the cap keeps
  // `asin` finite on the way there.
  const ratio = Math.min(0.999, radiusM / Math.max(1, distM));
  const discPx = radiusM > 0 ? (Math.tan(Math.asin(ratio)) / tanHalf) * (height / 2) : 0;
  return {
    front,
    px: ((ndcX + 1) / 2) * width,
    py: ((1 - ndcY) / 2) * height,
    discPx,
    distM,
  };
}

/**
 * The id of the nearer body whose disc covers `p`'s centre, or null. A
 * screen-space test, with no raycast: only bodies in front of the eye and
 * nearer than `p` can hide it.
 */
export function occluderOf(
  p: Projected, occluders: ReadonlyArray<{ readonly id: string; readonly p: Projected }>, selfId: string,
): string | null {
  if (!p.front) return null;
  for (const o of occluders) {
    if (o.id === selfId || !o.p.front || o.p.distM >= p.distM) continue;
    if (Math.hypot(p.px - o.p.px, p.py - o.p.py) < o.p.discPx) return o.id;
  }
  return null;
}

/** A disc wider than this share of the shorter screen side fills the view. */
export const FILLS_VIEW_SHARE = 0.5;

/**
 * Where a name's own box sits: centred over the body and just above its disc
 * edge. A body whose disc fills the view keeps its name on screen at the
 * disc's top edge even while its centre is off it. Null when the name has no
 * place at all: behind the eye, or off screen.
 */
export function labelAnchor(
  p: Projected, boxW: number, boxH: number, width: number, height: number,
  gapPx: number, marginPx: number,
): { x: number; bottom: number } | null {
  if (!p.front) return null;
  const onScreen = p.px >= 0 && p.px <= width && p.py >= 0 && p.py <= height;
  const fills = p.discPx >= Math.min(width, height) * FILLS_VIEW_SHARE;
  if (!onScreen) {
    if (!fills) return null;
    // The disc must still reach into the frame for its edge to be seen.
    const nx = Math.max(0, Math.min(width, p.px));
    const ny = Math.max(0, Math.min(height, p.py));
    if (Math.hypot(nx - p.px, ny - p.py) > p.discPx) return null;
  }
  const bottom = p.py - p.discPx - gapPx;
  if (!fills) return { x: p.px, bottom };
  const halfW = boxW / 2;
  return {
    x: Math.max(marginPx + halfW, Math.min(width - marginPx - halfW, p.px)),
    bottom: Math.max(marginPx + boxH, Math.min(height - marginPx, bottom)),
  };
}
